<template>
  <div class="product-types-settings">

    <!-- add product type button -->
    <div class="d-flex justify-space-between align-center flex-wrap my-4">
      <h3>أنواع المنتجات</h3>
      <v-btn color="primary" @click="openAdd">
        <v-icon left>mdi-plus</v-icon>
        إضافة نوع منتج
      </v-btn>
    </div>

    <!-- filters card -->
    <v-card class="mb-4">
      <v-card-text>
        <div class="d-flex flex-wrap align-center" style="gap: 12px">
          <v-text-field
            v-model="filters.search"
            label="بحث (الاسم، القيمة، الوصف)"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="max-width: 320px"
            @input="onSearchInput"
            @click:clear="applyFilters"
          />
          <v-select
            v-model="filters.active"
            :items="activeOptions"
            item-text="label"
            item-value="value"
            label="الحالة"
            outlined
            dense
            hide-details
            style="max-width: 200px"
            @change="applyFilters"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- products table -->
    <table-component
      :showSelect="false"
      itemKey="id"
      :headers="headers"
      :pagination="pagination"
      :limit="$store.state.counterOfShow"
      :items="items"
      :loading="loading"
      v-model="page"
      @refresh="onTableRefresh"
    >
      <template v-slot:[`item.image`]="{ item }">
        <div class="image-cell">
          <v-avatar v-if="item.image_url" size="44" tile class="product-thumb">
            <v-img :src="item.image_url" :alt="item.name" contain />
          </v-avatar>
          <span v-else class="no-image-text">—</span>
        </div>
      </template>

      <template v-slot:[`item.value`]="{ item }">
        <span class="value-cell" dir="ltr">{{ item.value }}</span>
      </template>

      <template v-slot:[`item.active`]="{ item }">
        <v-chip
          :color="item.active ? 'success' : 'error'"
          dark
          label
          small
          class="status-chip font-weight-medium"
        >
          <v-icon small class="status-chip__icon">
            {{ item.active ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
          <span>{{ item.active ? "نشط" : "غير نشط" }}</span>
        </v-chip>
      </template>

      <template v-slot:[`item.site_show`]="{ item }">
        <v-btn
          :color="item.site_show ? 'success' : 'error'"
          fab
          x-small
          dark
          depressed
          class="site-show-btn"
        >
          <v-icon small>
            {{ item.site_show ? "mdi-eye" : "mdi-eye-off" }}
          </v-icon>
        </v-btn>
      </template>

      <template v-slot:[`item.agents_total_count`]="{ item }">
        <span class="font-weight-medium">{{ item.agents_total_count || 0 }}</span>
        <small class="agents-sub d-block">
          داخلي {{ item.agents_internal_count || 0 }} / خارجي {{ item.agents_external_count || 0 }}
        </small>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-center actions-cell">
          <v-btn
            color="blue darken-1"
            fab
            x-small
            dark
            depressed
            class="action-btn"
            @click="openEdit(item)"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            color="error"
            fab
            x-small
            dark
            depressed
            class="action-btn"
            @click="openDelete(item)"
          >
            <v-icon small>mdi-trash-can-outline</v-icon>
          </v-btn>
        </div>
      </template>
    </table-component>

    <!-- product type form [add or edit] dialog -->
    <ProductTypeForm v-model="dialog" :edit-id="editId" @saved="fetchItems" />

    <!-- delete product type dialog -->
    <v-dialog v-model="deleteDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="headline">حذف نوع المنتج</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف «{{ deleteTarget?.name }}»؟
          <div v-if="deleteTarget?.agents_total_count > 0" class="mt-2 warning--text">
            سيتم حذف {{ deleteTarget.agents_total_count }} ربط من product_type_agents تلقائياً.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">تأكيد الحذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import Axios from "axios";
import ProductTypeForm from "./ProductTypeForm.vue";

export default {
  name: "AdminProductTypes",
  components: { ProductTypeForm },
  data() {
    return {
      loading: false,
      items: [],
      pagination: {
        limit: 10,
        count: 0,
      },
      page: 1,
      dialog: false,
      editId: null,
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      searchTimer: null,
      filters: {
        search: "",
        active: "all",
      },
      activeOptions: [
        { label: "الكل", value: "all" },
        { label: "نشط", value: 1 },
        { label: "غير نشط", value: 0 },
      ],
      headers: [
        { text: "#", value: "id", width: "70px", sortable: false },
        { text: "الصورة", value: "image", width: "80px", sortable: false, align: "center" },
        { text: "الاسم", value: "name", sortable: false },
        { text: "القيمة (value)", value: "value", sortable: false },
        { text: "الترتيب", value: "sort", width: "90px", sortable: false },
        { text: "نشط", value: "active", sortable: false, align: "center" },
        { text: "الموقع", value: "site_show", sortable: false, align: "center" },
        { text: "الاستشاريون", value: "agents_total_count", sortable: false },
        { text: "تاريخ الإنشاء", value: "created_at", sortable: false },
        { text: "العمليات", value: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    apiBase() {
      return `${this.$store.state.url}/employee/admin/product-types`;
    },
  },
  watch: {
    page() {
      this.fetchItems();
    },
    "$store.state.counterOfShow"(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchItems();
      }
    },
  },
  created() {
    this.fetchItems();
  },
  methods: {
    parseListPayload(payload) {
      const block = payload?.data;
      const rows = Array.isArray(block) ? block : block?.data || [];
      const total =
        payload?.count ??
        block?.meta?.total ??
        block?.total ??
        rows.length;
      return { rows, total };
    },
    onTableRefresh() {
      this.page = 1;
      this.fetchItems();
    },
    async fetchItems() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          per_page: this.$store.state.counterOfShow,
        };
        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.active !== "all") params.active = this.filters.active;

        const res = await Axios.get(this.apiBase, {
          params,
          headers: this.request_headers(),
        });
        const { rows, total } = this.parseListPayload(res.data.payload || {});
        this.items = rows;
        this.pagination = {
          limit: this.$store.state.counterOfShow,
          count: total,
        };
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ أثناء التحميل");
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.fetchItems();
      }
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applyFilters(), 500);
    },
    openAdd() {
      this.editId = null;
      this.dialog = true;
    },
    openEdit(item) {
      this.editId = item.id;
      this.dialog = true;
    },
    openDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        await Axios.delete(`${this.apiBase}/${this.deleteTarget.id}`, {
          headers: this.request_headers(),
        });
        this.showPopup("success", "تم الحذف بنجاح");
        this.deleteDialog = false;
        this.deleteTarget = null;
        if (this.items.length === 1 && this.page > 1) {
          this.page -= 1;
        } else {
          this.fetchItems();
        }
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "تعذر الحذف");
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.product-types-settings {
  padding: 16px;
}

.value-cell {
  font-family: monospace;
  font-size: 0.875rem;
  letter-spacing: 0.3px;
}

.image-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-thumb {
  border-radius: 6px;
  border: 1px solid rgba(140, 140, 140, 0.3);
  background: rgba(0, 0, 0, 0.03);
}

.no-image-text {
  color: rgba(0, 0, 0, 0.38);
}

.theme--dark .product-thumb,
.dark .product-thumb {
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.04);
}

.theme--dark .no-image-text,
.dark .no-image-text {
  color: rgba(255, 255, 255, 0.45);
}

.status-chip {
  cursor: default;
}
.status-chip__icon {
  margin-inline-end: 6px;
}

.agents-sub {
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.75rem;
}

.actions-cell {
  gap: 10px;
}
.action-btn {
  margin: 0;
}

.product-types-settings >>> .main__table .v-data-table td .v-btn--fab.v-size--x-small,
.product-types-settings >>> .main__table .v-data-table td .site-show-btn {
  height: 24px;
  width: 24px;
}
.product-types-settings >>> .main__table .v-data-table td .v-btn--fab.v-size--x-small .v-icon {
  font-size: 15px;
}

.theme--dark .agents-sub,
.dark .agents-sub {
  color: rgba(255, 255, 255, 0.6);
}
</style>
